// Typed client for the patients / consultations / clinical-history endpoints.
//
// The only place in the frontend that calls `api.*` for these domains. Every
// method maps the wire DTO through a pure parse adapter so callers always
// receive the domain form (timestamps as `Date`). No business logic beyond
// parsing lives here — querying/caching is the hooks' job.

import { api, ApiError, clinicApi } from '@/lib/api';
import type {
  AutosaveConsultationInput,
  ClinicalHistory,
  ClinicalHistoryDTO,
  ClinicalHistoryPayload,
  Consultation,
  ConsultationDTO,
  CreatePatientInput,
  FinalizeConsultationInput,
  Patient,
  PatientDTO,
  QuickCreatePatientInput,
  StartConsultationInput,
  UpdatePatientInput,
} from '@/types/patients';

// ---------- Parse adapters (wire → domain) ----------

// Convert an ISO timestamp from the wire into a Date, failing loudly rather than
// letting an `Invalid Date` (NaN) leak into the UI where it surfaces as a silent
// "Invalid Date" string or NaN math far from the cause.
function toDate(iso: string): Date {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    throw new ApiError('PARSE_ERROR', `Invalid timestamp from server: ${iso}`, 0);
  }
  return d;
}

export function parsePatient(dto: PatientDTO): Patient {
  return {
    ...dto,
    // dateOfBirth intentionally left as a `yyyy-mm-dd` string.
    createdAt: toDate(dto.createdAt),
    updatedAt: toDate(dto.updatedAt),
    deletedAt: dto.deletedAt === null ? null : toDate(dto.deletedAt),
  };
}

export function parseConsultation(dto: ConsultationDTO): Consultation {
  return {
    ...dto,
    visitDate: toDate(dto.visitDate),
    draftUpdatedAt: dto.draftUpdatedAt === null ? null : toDate(dto.draftUpdatedAt),
    finalizedAt: dto.finalizedAt === null ? null : toDate(dto.finalizedAt),
  };
}

export function parseHistory(dto: ClinicalHistoryDTO): ClinicalHistory {
  return {
    ...dto,
    updatedAt: toDate(dto.updatedAt),
  };
}

// ---------- Patients ----------

interface SearchParams {
  q?: string;
  limit?: number;
  offset?: number;
}

export const patientsApi = {
  async search(params: SearchParams = {}): Promise<Patient[]> {
    const { q, limit, offset } = params;
    const qs = new URLSearchParams();
    const trimmed = q?.trim() ?? '';
    if (trimmed) qs.set('q', trimmed);
    if (limit !== undefined) qs.set('limit', String(limit));
    if (offset !== undefined) qs.set('offset', String(offset));
    const query = qs.toString();
    const dtos = await clinicApi.get<PatientDTO[]>(`patients${query ? `?${query}` : ''}`);
    return dtos.map(parsePatient);
  },

  async recent(params: { limit?: number } = {}): Promise<Patient[]> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    const query = qs.toString();
    const dtos = await clinicApi.get<PatientDTO[]>(`patients/recent${query ? `?${query}` : ''}`);
    return dtos.map(parsePatient);
  },

  async create(input: CreatePatientInput): Promise<Patient> {
    return parsePatient(await clinicApi.post<PatientDTO>('patients', input));
  },

  async quickCreate(input: QuickCreatePatientInput): Promise<Patient> {
    return parsePatient(await clinicApi.post<PatientDTO>('patients/quick', input));
  },

  async get(id: string): Promise<Patient> {
    return parsePatient(await clinicApi.get<PatientDTO>(`patients/${id}`));
  },

  async update(id: string, patch: UpdatePatientInput): Promise<Patient> {
    return parsePatient(await clinicApi.patch<PatientDTO>(`patients/${id}`, patch));
  },

  async remove(id: string): Promise<void> {
    await clinicApi.delete<{ deleted: true }>(`patients/${id}`);
  },

  // ---------- Photo (presigned S3) ----------

  /** Step 1: ask the server for a presigned PUT URL + the object key to confirm. */
  async requestPhotoUploadUrl(
    patientId: string,
    contentType: string,
  ): Promise<{ uploadUrl: string; key: string; expiresIn: number }> {
    return clinicApi.post<{ uploadUrl: string; key: string; expiresIn: number }>(`patients/${patientId}/photo/upload-url`,
      { contentType },
    );
  },

  /** Step 3: confirm the upload landed; the server persists it and returns the patient. */
  async attachPhoto(patientId: string, key: string): Promise<Patient> {
    return parsePatient(await clinicApi.put<PatientDTO>(`patients/${patientId}/photo`, { key }));
  },

  async removePhoto(patientId: string): Promise<Patient> {
    // The route returns the updated patient (200 with body), never 204.
    const dto = await clinicApi.delete<PatientDTO>(`patients/${patientId}/photo`);
    return parsePatient(dto as PatientDTO);
  },

  /**
   * Full upload flow for a captured photo: get a presigned URL, PUT the bytes
   * DIRECTLY to S3 (not through `api` — no bearer header, different origin), then
   * confirm. The bytes never pass through our backend.
   */
  async uploadPhoto(patientId: string, blob: Blob, contentType: string): Promise<Patient> {
    const { uploadUrl, key } = await this.requestPhotoUploadUrl(patientId, contentType);
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: blob,
    });
    if (!res.ok) {
      throw new ApiError('UPLOAD_FAILED', `Photo upload failed (status ${res.status})`, res.status);
    }
    return this.attachPhoto(patientId, key);
  },
};

// ---------- Consultations ----------

export const consultationsApi = {
  async startDraft(patientId: string, input: StartConsultationInput = {}): Promise<Consultation> {
    return parseConsultation(
      await clinicApi.post<ConsultationDTO>(`patients/${patientId}/consultations`, input),
    );
  },

  async list(
    patientId: string,
    params: { limit?: number; offset?: number } = {},
  ): Promise<Consultation[]> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.offset !== undefined) qs.set('offset', String(params.offset));
    const query = qs.toString();
    const dtos = await clinicApi.get<ConsultationDTO[]>(`patients/${patientId}/consultations${query ? `?${query}` : ''}`,
    );
    return dtos.map(parseConsultation);
  },

  async get(id: string): Promise<Consultation> {
    return parseConsultation(await clinicApi.get<ConsultationDTO>(`consultations/${id}`));
  },

  async autosave(id: string, body: AutosaveConsultationInput): Promise<Consultation> {
    return parseConsultation(
      await clinicApi.put<ConsultationDTO>(`consultations/${id}/autosave`, body),
    );
  },

  async finalize(id: string, input: FinalizeConsultationInput = {}): Promise<Consultation> {
    return parseConsultation(
      await clinicApi.post<ConsultationDTO>(`consultations/${id}/finalize`, input),
    );
  },
};

// ---------- Clinical history ----------

export const historyApi = {
  async get(patientId: string): Promise<ClinicalHistory> {
    return parseHistory(await clinicApi.get<ClinicalHistoryDTO>(`patients/${patientId}/history`));
  },

  async upsert(patientId: string, payload: ClinicalHistoryPayload): Promise<ClinicalHistory> {
    return parseHistory(
      await clinicApi.put<ClinicalHistoryDTO>(`patients/${patientId}/history`, { payload }),
    );
  },
};

// ---------- Suggestion catalogue ----------

/** The kinds of free-text picklist the clinic-wide catalogue tracks. */
export type SuggestionKind = 'referral' | 'investigation' | 'finding' | 'diagnosis';

export const suggestionsApi = {
  /**
   * The clinic's previously-used catalogue values for a kind, ranked by usage.
   * Bare strings — callers merge these with their static preset pool. `finding`
   * values are scoped to a body system via `system` (a `SystemsReview` key);
   * the clinic-global kinds omit it.
   */
  async list(
    kind: SuggestionKind,
    opts: { system?: string; limit?: number } = {},
  ): Promise<string[]> {
    const params = new URLSearchParams({
      kind,
      limit: String(opts.limit ?? 30),
    });
    if (opts.system) params.set('system', opts.system);
    return clinicApi.get<string[]>(`suggestions?${params.toString()}`);
  },
};
